import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import API from "../../config/api";
import TaxonomyChips from "../../Components/TaxonomyChips/TaxonomyChips";
import useSeo from "../../hooks/useSeo";
import { useTranslation } from "../../i18n/LanguageProvider";
import { getInitialData } from "../../utils/initialData";
import { getWomanThumbImageUrl } from "../../utils/imageUrl";
import { buildNotFoundSeo, buildTaxonomyCopy, buildTaxonomySeo, getStaticSeoForPath } from "../../utils/seo";
import { getIdFromSlug, toTaxonomyPath } from "../../utils/slug";
import WomanCard from "../Home/Components/WomanCard/WomanCard";
import NotFound from "../NotFound/NotFound";
import "./Directory.sass";

function getMatchingInitialPage(type, id, page) {
    const initial = getInitialData("taxonomyPage");

    if (initial && initial.type === type && initial.item?.id === id && initial.page === page) {
        return initial;
    }

    return null;
}

/**
 * /{lang}/categorie/{slug}_{id} and /{lang}/tara/{slug}_{id} - the profiles of one category or country.
 */
function TaxonomyPage({ type }) {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const { language, t, localizedPath } = useTranslation();
    const id = getIdFromSlug(slug);
    const page = Math.max(1, parseInt(searchParams.get("page"), 10) || 1);
    const [data, setData] = useState(() => getMatchingInitialPage(type, id, page));
    const [loading, setLoading] = useState(() => !getMatchingInitialPage(type, id, page));
    const [notFound, setNotFound] = useState(false);
    const seo = useMemo(() => {
        if (notFound) return buildNotFoundSeo(language);
        if (data) return buildTaxonomySeo(data, language);

        return getStaticSeoForPath("/femei", language);
    }, [data, language, notFound]);

    useSeo(seo);

    useEffect(() => {
        window.scrollTo(0, 0);
        setNotFound(false);

        const initial = getMatchingInitialPage(type, id, page);

        if (initial) {
            setData(initial);
            setLoading(false);
            return undefined;
        }

        if (!id) {
            setData(null);
            setNotFound(true);
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        const params = new URLSearchParams({ taxonomyPage: type, id, page, lang: language });

        setLoading(true);

        fetch(`${API.getWoman}?${params.toString()}`, { credentials: "include" })
            .then((res) => res.json())
            .then((response) => {
                if (cancelled) return;

                if (response?.data) {
                    setData(response.data);
                } else {
                    setData(null);
                    setNotFound(true);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setData(null);
                    setNotFound(true);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [id, language, page, type]);

    if (notFound) {
        return <NotFound />;
    }

    if (loading || !data) {
        return (
            <main className="directory general-wrapper">
                <p className="directory__loading">{t("directory.loading")}</p>
            </main>
        );
    }

    const copy = buildTaxonomyCopy(data, language);
    const otherType = type === "category" ? "place" : "category";
    const basePath = localizedPath(toTaxonomyPath(type, data.item));
    const pagePath = (target) => (target > 1 ? `${basePath}?page=${target}` : basePath);

    return (
        <main className="directory general-wrapper">
            <nav className="directory__breadcrumb">
                <Link to={localizedPath("/")}>{t("profile.home")}</Link>
                <span> / </span>
                <Link to={localizedPath("/femei")}>{t("directory.breadcrumb")}</Link>
                <span> / </span>
                <span>{data.item.name}</span>
            </nav>
            <h1 className="directory__heading">{copy.heading}</h1>

            <div className="directory__summary card-common">
                <p className="directory__intro">{copy.intro}</p>
                {data.breakdown?.length > 0 && (
                    <>
                        <p className="directory__breakdown-label">{copy.breakdownLabel}</p>
                        <TaxonomyChips type={otherType} items={data.breakdown} />
                    </>
                )}
            </div>

            {data.women?.length > 0 ? (
                <div className="cards">
                    {data.women.map((woman) => (
                        <WomanCard
                            key={woman.id}
                            id={woman.id}
                            name={woman.name}
                            surname={woman.surname}
                            domain={woman.category_name}
                            categoryColor={woman.category_color}
                            image={getWomanThumbImageUrl(woman.image)}
                        />
                    ))}
                </div>
            ) : (
                <p className="directory__empty">{t("directory.empty")}</p>
            )}

            {data.totalPages > 1 && (
                <nav className="directory__pagination" aria-label={copy.pageInfo}>
                    {data.page > 1 && (
                        <Link className="directory__page-link" rel="prev" to={pagePath(data.page - 1)}>
                            {t("directory.prev")}
                        </Link>
                    )}
                    <span className="directory__page-info">{copy.pageInfo}</span>
                    {data.page < data.totalPages && (
                        <Link className="directory__page-link" rel="next" to={pagePath(data.page + 1)}>
                            {t("directory.next")}
                        </Link>
                    )}
                </nav>
            )}
        </main>
    );
}

export default TaxonomyPage;
